import * as React from 'react';
import { ProductInterface } from '@westwing/appshell_types';
import ArrowIcon from '@westwing/ui-kit/Icons/ArrowIcon';
import { Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import { Product, ProductInfoBox } from '@westwing/ui-kit/Product';
import Slider, { SliderProvider, useSlider } from './Slider';
import * as Styled from './OurProjects.styles';
import BeforeAfterImageSlider from './BeforeAfterImageSlider';
import ProductPicture from './ProductPicture';
import CTAButton, { useCTAButtonTracking } from '@ids/components/CTAButton';
import * as Display from '@ids/components/Layout/Display';
import Center from '@ids/components/Layout/Center';
import { useOurProjectsBlock } from '@ids/context';
import { NormalizedLandingPageData } from '@ids/context/LandingPage/types';
import { preFillPDP, useTranslator } from '@ids/appshell';
import useIdsGaTracking from '@ids/hooks/useIdsGaTracking';
import Divider from '@ids/components/Layout/Divider';
import Link from '@ids/components/Link';
import { useCustomEvent } from '@ids/hooks';
import Skeleton from '@ids/components/Layout/Skeleton';

interface ProjectsSlidesProps {
    projects: NormalizedLandingPageData['ourProjectsBlock']['projects'];
}

const ProjectsSlides = ({ projects }: ProjectsSlidesProps) => {
    const { handleArrowClick, isFirstSlide, isLastSlide, trackSliderContentInteraction, currentSlide } = useSlider();
    const seenSlides = React.useRef([currentSlide]);
    const { trackIdsEvent } = useIdsGaTracking();
    const t = useTranslator();
    const { openNativePDP } = useCustomEvent();
    const ourProjectsBlock = useOurProjectsBlock();

    React.useEffect(() => {
        if (seenSlides.current && !seenSlides.current.includes(currentSlide)) {
            seenSlides.current.push(currentSlide);
        }
    }, [currentSlide]);

    const onClick = (sku: string) => {
        trackIdsEvent({
            action: 'Content interaction click',
            label: `Product click - ${sku}`,
        });
    };

    const onNativeAppClick = (sku: string) => {
        openNativePDP(sku);
    };

    const onWebClick = (product: ProductInterface) => {
        preFillPDP(product);
    };

    return (
        <Slider>
            {projects.map((project, idx) => (
                <Styled.Slide
                    key={project.id}
                    data-testid={`project-slide-${idx + 1}`}
                    data-is-seen-slide={seenSlides.current.includes(idx)}
                    data-is-current-slide={idx === currentSlide}
                    data-is-adjacent-slide={idx === currentSlide + 1 || idx === currentSlide - 1}
                >
                    <Styled.NextPrevButtonsWrapper>
                        <Styled.NextPrevButton
                            data-testid="previous-project-button"
                            aria-label="previous project"
                            onClick={e => {
                                trackSliderContentInteraction();
                                handleArrowClick(e, Direction.Left, isFirstSlide);
                            }}
                            disabled={isFirstSlide}
                        >
                            <ArrowIcon size={10} direction={Direction.Left} title={t('Previous')} />
                            <Styled.NextPrevButtonText>
                                {ourProjectsBlock?.previousProjectText}
                            </Styled.NextPrevButtonText>
                        </Styled.NextPrevButton>
                        <Styled.NextPrevButton
                            data-testid="next-project-button"
                            aria-label="next project"
                            onClick={e => {
                                trackSliderContentInteraction();
                                handleArrowClick(e, Direction.Right, isLastSlide);
                            }}
                            disabled={isLastSlide}
                        >
                            <Styled.NextPrevButtonText>{ourProjectsBlock?.nextProjectText}</Styled.NextPrevButtonText>
                            <ArrowIcon size={10} direction={Direction.Right} title={t('Next')} />
                        </Styled.NextPrevButton>
                    </Styled.NextPrevButtonsWrapper>
                    <BeforeAfterImageSlider beforeImage={project.beforeImage} afterImage={project.afterImage} />
                    <Styled.Content>
                        <Styled.ProjectTitleWrapper>
                            <Display.MobileOnly>
                                <Styled.ArrowButton
                                    aria-label="arrow left button"
                                    data-testid="arrow-left-button-mobile-only"
                                    disabled={isFirstSlide}
                                    onClick={e => {
                                        trackSliderContentInteraction();
                                        handleArrowClick(e, Direction.Left, isFirstSlide);
                                    }}
                                >
                                    <ArrowIcon size={12} direction={Direction.Left} title={t('Previous')} />
                                </Styled.ArrowButton>
                            </Display.MobileOnly>
                            <Styled.ProjectTitle>{project.title}</Styled.ProjectTitle>
                            <Display.MobileOnly>
                                <Styled.ArrowButton
                                    aria-label="arrow right button"
                                    data-testid="arrow-right-button-mobile-only"
                                    disabled={isLastSlide}
                                    onClick={e => {
                                        trackSliderContentInteraction();
                                        handleArrowClick(e, Direction.Right, isLastSlide);
                                    }}
                                >
                                    <ArrowIcon size={12} direction={Direction.Right} title={t('Next')} />
                                </Styled.ArrowButton>
                            </Display.MobileOnly>
                        </Styled.ProjectTitleWrapper>
                        <Styled.ProjectDescription>{project.contentText}</Styled.ProjectDescription>
                        <Styled.Products data-testid="products-grid">
                            {project.products.map(product => (
                                <Product
                                    key={product.sku}
                                    data-testid="product-grid-item"
                                    as={Link}
                                    to={product.link}
                                    onClick={() => onClick(product.sku)}
                                    onNativeAppClick={() => onNativeAppClick(product.sku)}
                                    onWebClick={() => onWebClick(product)}
                                >
                                    <ProductPicture product={product} />
                                    <ProductInfoBox>
                                        <ProductInfoBox.Title>{product.name}</ProductInfoBox.Title>
                                        <ProductInfoBox.BrandText>{product.brand}</ProductInfoBox.BrandText>
                                        <ProductInfoBox.Price price={product.originalPrice || product.price} />
                                    </ProductInfoBox>
                                </Product>
                            ))}
                        </Styled.Products>
                    </Styled.Content>
                </Styled.Slide>
            ))}
        </Slider>
    );
};

const OurProjects = () => {
    const ourProjectsBlock = useOurProjectsBlock();
    const trackCTAButtonClick = useCTAButtonTracking();

    if (!ourProjectsBlock || !ourProjectsBlock?.projects?.length) {
        return <Skeleton height="150vh" />;
    }

    return (
        <React.Fragment>
            <SliderProvider numSlides={ourProjectsBlock.projects.length}>
                <Styled.OurProjectsSection data-testid="our-projects-block-section" id="our-projects">
                    <Center.Vertical>
                        <Styled.Title>{ourProjectsBlock.title}</Styled.Title>
                        <Styled.ContentText>{ourProjectsBlock.contentText}</Styled.ContentText>
                    </Center.Vertical>
                    <ProjectsSlides projects={ourProjectsBlock.projects} />
                    <Center>
                        <CTAButton
                            data-testid="our-projects-cta-button"
                            onClick={() => trackCTAButtonClick('ga_projects_cta')}
                        >
                            {ourProjectsBlock.ctaText}
                        </CTAButton>
                    </Center>
                </Styled.OurProjectsSection>
            </SliderProvider>
            <Divider />
        </React.Fragment>
    );
};

export default OurProjects;
